import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Radio,
	RadioGroup,
	TextField as MuiTextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import axios from 'axios';
import moment from 'moment';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
	Datagrid,
	FunctionField,
	Identifier,
	List,
	ListContextProvider,
	ReferenceField,
	TextField,
	useListContext,
	useListController,
	useRefresh,
} from 'react-admin';
import { CgNotes, CgTime } from 'react-icons/cg';
import { FaDownload } from 'react-icons/fa';
import constants from '../../../constants';
import { downloadEncodedCSV, formatPrice } from '../../../utils/functions';
const MonthlySessionsStats = () => {
	const properties = [
		{ field: 'helpingHand', name: 'עזרה ראשונה' },
		{ field: 'consultationSessions', name: 'הכוונה' },
		{ field: 'oneToOneSessions', name: 'אחד על אחד' },
		{ field: 'groupSessions', name: 'הרצאות וסדנאות' },
		{ field: 'safe_space', name: 'מרחב בטוח' },
		{ field: 'wellness', name: 'תקציב ניהולי' },
	];
	const [date, setDate] = useState(new Date());
	const [nameSearch, setNameSearch] = useState('');

	// Client-side filter by expert name. All rows are loaded (perPage 9999), so we
	// filter in-memory and re-provide the list context to the grid + totals.
	const filterByName = (rows: any[] | undefined) => {
		const q = nameSearch.trim().toLowerCase();
		if (!q) return rows;
		return (rows || []).filter((r: any) => (r?.name || '').toLowerCase().includes(q));
	};

	const FilteredGrid = ({ children }: { children: ReactNode }) => {
		const ctx = useListContext();
		const data = useMemo(() => filterByName(ctx.data) ?? [], [ctx.data, nameSearch]);
		return <ListContextProvider value={{ ...ctx, data, total: data.length }}>{children}</ListContextProvider>;
	};

	const Aside = () => {
		const formatter = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'ILS',
		});
		const context = useListContext();
		const data = filterByName(context.data);

		const total = data?.reduce((curr, next) => (curr += next.total?.price), 0);
		const totalGroupSessions = data?.reduce((curr, next) => (curr += next.groupSessions?.price), 0);
		const totalConsultations = data?.reduce((curr, next) => (curr += next.consultationSessions?.price), 0);

		return (
			<Box paddingBottom={1} display={'flex'} flexDirection="row" justifyContent={'center'} gap={2}>
				<Typography>
					{`סה"כ לתשלום לתקופה הנבחרת: `}
					<Typography display="inline" fontWeight={'bold'}>{`${formatter.format(total) ?? '-'}`}</Typography>
				</Typography>
				<Typography>
					{`הרצאות וסדנאות: `}
					<Typography
						display="inline"
						fontWeight={'bold'}
					>{`${formatter.format(totalGroupSessions) ?? '-'}`}</Typography>
				</Typography>
				<Typography>
					{`הכוונה: `}
					<Typography
						display="inline"
						fontWeight={'bold'}
					>{`${formatter.format(totalConsultations) ?? '-'}`}</Typography>
				</Typography>
			</Box>
		);
	};
	const month = moment(date).month();
	const refresh = useRefresh();
	const year = moment(date).year();
	const [isLoading, setIsLoading] = useState(false);
	const updateMonthlySessions = async () => {
		try {
			setIsLoading(true);
			await axios.post(
				`${constants.API_URL}/instructor/sessions/stats/update`,
				{},
				{ params: { filter: { month, year } } },
			);
			refresh();
		} catch (error) {
		} finally {
			setIsLoading(false);
		}
	};
	useEffect(() => {
		// updateMonthlySessions();
	}, [month, year]);
	const { data } = useListController({ filter: { month, year } });

	// GH-694 (Udi): month-end "עדכון לגבי תשלום" email to every expert with a payout for the month.
	// The accountant pays from this table independently; the button only notifies the experts.
	const MONTHS_HE = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
	const [notifyOpen, setNotifyOpen] = useState(false);
	const [notifyMode, setNotifyMode] = useState<'missing' | 'all'>('missing');
	const [notifying, setNotifying] = useState(false);
	const [notifyResult, setNotifyResult] = useState<string | null>(null);
	// Preview: the exact email each expert will get (rendered by the backend with the same builder).
	const [preview, setPreview] = useState<
		Array<{ expert_id: string; name: string; email: string | null; language: string; amount: number; already_notified_at: string | null; subject: string; html: string }> | null
	>(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const loadPreview = async () => {
		try {
			setPreviewLoading(true);
			const res = await axios.get(`${constants.API_URL}/instructor/sessions/stats/notify-paid/preview`, { params: { month, year } });
			setPreview(res.data?.items || []);
		} catch (error: any) {
			setNotifyResult(`טעינת התצוגה המקדימה נכשלה: ${error?.response?.data?.message || error?.message}`);
		} finally {
			setPreviewLoading(false);
		}
	};
	const payable = (data || []).filter((r: any) => (r?.total?.price || 0) > 0);
	const alreadyNotified = payable.filter((r: any) => r.payment_notified_at).length;
	const sendPaymentUpdates = async () => {
		try {
			setNotifying(true);
			const res = await axios.post(`${constants.API_URL}/instructor/sessions/stats/notify-paid`, {
				month,
				year,
				resend: notifyMode,
			});
			const d = res.data || {};
			setNotifyResult(
				`נשלחו ${d.sent ?? 0} מיילים עבור ${MONTHS_HE[month]} ${year}` +
					(d.skipped_already_notified ? ` · דולגו ${d.skipped_already_notified} שכבר קיבלו` : '') +
					(d.skipped_no_email ? ` · ${d.skipped_no_email} בלי מייל` : '') +
					(d.failed?.length ? ` · נכשלו: ${d.failed.join(', ')}` : ''),
			);
			refresh();
		} catch (error: any) {
			setNotifyResult(`השליחה נכשלה: ${error?.response?.data?.message?.he || error?.response?.data?.message || error?.message}`);
		} finally {
			setNotifying(false);
		}
	};
	return (
		<Box marginTop={2}>
			<Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
				<MuiTextField
					size="small"
					label="חיפוש מומחה"
					style={{ alignSelf: 'center', minWidth: 240 }}
					value={nameSearch}
					onChange={(e) => setNameSearch(e.target.value)}
				/>
				<Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={1}>
					<MuiTextField
						style={{ alignSelf: 'center' }}
						value={moment(date).format('YYYY-MM')}
						type="month"
						onChange={(e) => {
							setDate(new Date(e.target.value));
						}}
					/>
					<Button style={{ alignSelf: 'center' }} onClick={updateMonthlySessions}>
						עדכון נתונים
					</Button>
					<Button
						variant="outlined"
						style={{ alignSelf: 'center', whiteSpace: 'nowrap' }}
						disabled={isLoading}
						onClick={() => {
							setNotifyMode('missing');
							setNotifyResult(null);
							setPreview(null);
							setNotifyOpen(true);
						}}
					>
						שלח עדכון תשלום למומחים
					</Button>
				</Box>
			</Box>
			<Dialog open={notifyOpen} onClose={() => !notifying && setNotifyOpen(false)} dir="rtl" maxWidth={preview ? 'md' : 'sm'} fullWidth={Boolean(preview)}>
				<DialogTitle>עדכון לגבי תשלום — {MONTHS_HE[month]} {year}</DialogTitle>
				<DialogContent>
					{notifyResult ? (
						<Typography>{notifyResult}</Typography>
					) : (
						<>
							<Typography gutterBottom>
								יישלח מייל "עדכון לגבי תשלום" (עם החודש והסכום) לכל מומחה/מטפל שמגיע לו תשלום עבור החודש, מה-1 בחודש.
								הסכומים יחושבו מחדש לפני השליחה.
							</Typography>
							<Typography gutterBottom>
								מומחים עם תשלום: <b>{payable.length}</b>
								{alreadyNotified ? ` · כבר קיבלו מייל לחודש הזה: ${alreadyNotified}` : ''}
							</Typography>
							{alreadyNotified ? (
								<RadioGroup value={notifyMode} onChange={(e) => setNotifyMode(e.target.value as 'missing' | 'all')}>
									<FormControlLabel value="missing" control={<Radio />} label={`רק למי שעוד לא קיבל (${payable.length - alreadyNotified})`} />
									<FormControlLabel value="all" control={<Radio />} label={`לכולם שוב (${payable.length})`} />
								</RadioGroup>
							) : null}
							{!preview ? (
								<Button size="small" onClick={loadPreview} disabled={previewLoading} style={{ marginTop: 8 }}>
									{previewLoading ? 'טוען…' : 'תצוגה מקדימה — מה כל מומחה יקבל'}
								</Button>
							) : (
								<Box style={{ maxHeight: '55vh', overflowY: 'auto', marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}>
									{preview.map((it, i) => (
										<Box key={it.expert_id} style={{ padding: 12, borderBottom: i < preview.length - 1 ? '1px solid #e5e7eb' : undefined }}>
											<Typography fontWeight="bold">
												{i + 1}. {it.name} · {it.email || 'אין מייל — לא יישלח'} · {formatPrice(it.amount)}₪ · {it.language === 'en' ? 'EN' : 'HE'}
												{it.already_notified_at ? ` · כבר נשלח ${moment(it.already_notified_at).format('DD/MM')}` : ''}
											</Typography>
											<Typography variant="body2" color="text.secondary" style={{ marginBottom: 6 }}>
												נושא: {it.subject}
											</Typography>
											<div
												style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 6 }}
												dangerouslySetInnerHTML={{ __html: it.html }}
											/>
										</Box>
									))}
								</Box>
							)}
						</>
					)}
				</DialogContent>
				<DialogActions>
					{notifyResult ? (
						<Button onClick={() => setNotifyOpen(false)}>סגור</Button>
					) : (
						<>
							<Button onClick={() => setNotifyOpen(false)} disabled={notifying}>
								ביטול
							</Button>
							<Button variant="contained" onClick={sendPaymentUpdates} disabled={notifying || !payable.length}>
								{notifying ? 'שולח…' : 'שלח'}
							</Button>
						</>
					)}
				</DialogActions>
			</Dialog>
			<List
				actions={
					<div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
						<Button
							style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}
							onClick={(e) => {
								console.log('data',data);
								downloadEncodedCSV(
									data
										.filter((x) => x.total.price)
										.map((row) => ({ 
											name: row.name, 
											account_number: row.account_number || '',
											bank_name: row.bank_name || '',
											branch_number: row.branch_number || '',
											total: formatPrice(row.total.price)
										}))
										.sort((a, b) => a.name.localeCompare(b.name)),
									['name', 'account_number', 'bank_name', 'branch_number', 'total'],
									`monthly_sessions_${month}_${year}.csv`,
								);
								e.preventDefault();
								return;
							}}
						>
							Export
							<FaDownload />
						</Button>
						<Aside />
					</div>
				}
				filter={{ month, year }}
				perPage={9999}
			>
				<FilteredGrid>
				<Datagrid
					isLoading={isLoading}
					bulkActionButtons={false}
					rowClick={(id: Identifier) =>
						`/${'instructor/sessions/stats/by_host'}/${id}?month=${month}&year=${year}`
					}
				>
					<ReferenceField source="_id" reference="instructor/all" sortBy="name">
						<TextField source="name" />
					</ReferenceField>
					<FunctionField label="עוסק" render={(record: any) => record.licensed_dealer && 'ע. מורשה'} />
					{properties.map((prop) => (
						<FunctionField
							key={prop.field}
							render={(record: any) =>
								record[prop.field]?.count ? (
									<Tooltip
										title={
											record[prop.field]?.info ? (
												<div style={{ whiteSpace: 'pre-line' }}>{record[prop.field].info}</div>
											) : null
										}
										style={{ whiteSpace: 'pre-line' }}
									>
										<Typography display={'inline'} whiteSpace={'pre-wrap'}>


											{`${formatPrice(record[prop.field].price)}₪`}
											<Typography
												display={'inline'}
												variant="caption"
											>{` (${record[prop.field].count})`}</Typography>
										</Typography>
									</Tooltip>
								) : null
							}
							label={prop.name}
						/>
					))}
					<FunctionField render={(record: any) => (record.is_psychologist ? 'פסיכולוג.ית' : '')} />
					<FunctionField
						label="תוספת חד פעמית"
						render={(record: any) => (record.fixedPayment ? `${record.fixedPayment} ₪` : null)}
					/>
					<FunctionField
						label="ניכוי מעמ"
						dir="ltr"
						render={(record: any) =>
							record.vat_refund.price ? `${formatPrice(record.vat_refund.price)}₪` : ''
						}
					/>
					<FunctionField
						render={(record: any) =>
							record.notes || record.fixedPayment ? (
								<Tooltip
									title={
										<div
											style={{ whiteSpace: 'pre-line' }}
										>{`${record.notes ? `הערות:\n${record.notes}` : ''}${
											record.notes && record.fixedPayment ? '\n\n' : ''
										}${record.fixedPayment ? `תשלום חד פעמי: ${record.fixedPayment} ₪` : ''}`}</div>
									}
									style={{ whiteSpace: 'pre-line', zIndex: 10 }}
								>
									<span style={{ color: 'orangered', fontSize: 16 }}>
										<CgNotes />
									</span>
								</Tooltip>
							) : null
						}
					/>
					<FunctionField
						textAlign="right"
						label="עלות כוללת"
						render={(record: any) => `${formatPrice(record?.total.price)} ₪`}
					/>
					<FunctionField
						align="center"
						textAlign="right"
						label="מייל תשלום"
						render={(record: any) =>
							record.payment_notified_at ? (
								<Tooltip title={`מייל "עדכון לגבי תשלום" נשלח ${moment(record.payment_notified_at).format('DD/MM/YYYY HH:mm')}`}>
									<span style={{ color: 'green', whiteSpace: 'nowrap' }}>✓ {moment(record.payment_notified_at).format('DD/MM')}</span>
								</Tooltip>
							) : (
								''
							)
						}
					/>
					<FunctionField
						align="center"
						textAlign="right"
						label="עדכון אחרון"
						render={(record: any) => (
							<Tooltip
								title={moment(record.updatedAt).locale('he-IL').fromNow()}
								style={{ whiteSpace: 'pre-line', zIndex: 10 }}
							>
								<span
									style={{
										color: moment().diff(record.updatedAt, 'hour') > 1 ? 'red' : 'black',
										textAlign: 'center',
									}}
								>
									<CgTime />
								</span>
							</Tooltip>
						)}
					/>
				</Datagrid>
				</FilteredGrid>
			</List>
		</Box>
	);
};

export default MonthlySessionsStats;
